import React from "react";
import {
  Stack,
  Typography,
  styled,
  Box,
  Collapse,
  Button,
  IconButton,
  ListItemIcon,
  MenuItem,
  Grid,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import MoreMenu from "../../Misc/MoreMenu";
import { BasketPanelItems } from "../BasketPanel/basket-panel";
import { thumbnailService } from "../../../Core/Api/services";
import { BasketQuickviewItem } from "../../../Core/Api/Types/basket-quickview-item";
import OrderProductIcon from "../../Misc/OrderProductIcon/order-product-icon";
import { RemoveItem } from "../../../Redux/Reducers/redux-slice-basket";
import { useAppDispatch } from "../../../Redux/redux-store";
import useCollectionActions from "../../../Utils/Hooks/useCollectionActions";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { useTranslation } from "react-i18next";
export interface BasketItemProps {
  data: BasketPanelItems;
}

const BasketItem: React.FC<BasketItemProps> = (props) => {
  const { t } = useTranslation();
  const { data } = props;
  const [expanded, setExpanded] = React.useState(false);
  const { AddItemByIdToCollection } = useCollectionActions();

  const isExpandable = data.items.length > 0;

  // Redux
  const dispatch = useAppDispatch();

  const removeAllItems = () => {
    data.items.map((item) => {
      item.items.map((c) => {
        dispatch(RemoveItem(c.id!));
      });
    });
  };

  const addToCollection = (id: number) => {
    AddItemByIdToCollection(id);
  };

  const countItems = () => {
    let count = 0;
    data.items.map((item) => {
      item.items.map((c) => {
        ++count;
      });
    });

    return t("page:body.itemWithCount", { count });
  };

  return (
    <StyledBasketItem expanded={expanded}>
      <Stack alignItems="center">
        <Grid container>
          <Grid item md={8}>
            <Stack direction="row" spacing={2}>
              {data.parentLayer !== ObjectLayer.Group ? (
                <img src={thumbnailService.GetObjectPosterUrl(data.id)} alt="" width={48} height={64} loading="lazy" />
              ) : (
                <FolderOutlinedIcon />
              )}
              <Stack display={"grid"}>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  mb={0.5}
                  sx={(theme) => ({
                    ...theme.helpers(theme).clampedText,
                    wordWrap: "break-word",
                    overflow: "scroll",
                    scrollbarWidth: "none",
                  })}
                >
                  {data.name}
                </Typography>
                <Typography variant="body2" fontWeight={500} mb={1} color="text.tier">
                  {countItems()}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid item md={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <MoreMenu>
              <MenuItem onClick={() => addToCollection(data.id)}>
                <ListItemIcon>
                  <BookmarkAddOutlinedIcon fontSize="small" />
                </ListItemIcon>
                {t("button:addToCollection_one")}
              </MenuItem>
              <MenuItem sx={{ color: "error.main" }} onClick={() => removeAllItems()}>
                <ListItemIcon>
                  <DeleteOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
                </ListItemIcon>
                {t("common:remove")}
              </MenuItem>
            </MoreMenu>
          </Grid>
          <Grid item md={2} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            {isExpandable && (
              <IconButton onClick={() => setExpanded(!expanded)}>
                {expanded ? (
                  <KeyboardArrowDownIcon className="arrow-indicator" />
                ) : (
                  <KeyboardArrowRightIcon className="arrow-indicator" />
                )}
              </IconButton>
            )}
          </Grid>
        </Grid>
        {isExpandable && (
          <Collapse in={expanded} sx={{ width: "100%" }}>
            <Box mt={2}>
              {data.items.map((item) => (
                <BasketConfig title={item.layer!} list={item.items} key={item.layer} />
              ))}
            </Box>
          </Collapse>
        )}
      </Stack>
    </StyledBasketItem>
  );
};

const BasketConfig: React.FC<{
  title: string;
  list: BasketQuickviewItem[];
}> = (props) => {
  // Redux
  const dispatch = useAppDispatch();

  return (
    <>
      <Typography variant="h6" fontWeight={600} color="text.secondary" mb={1}>
        {props.list.length} {props.title}
      </Typography>
      <Stack spacing={0.5}>
        {props.list.map((c) => (
          <StyledBasketContent key={c.id}>
            {c.productType !== null && <OrderProductIcon type={c.productType} color="info" sx={{ mr: 2 }} />} {c.name}
            <Button variant="icon" size="small" onClick={() => dispatch(RemoveItem(c.id!))}>
              <DeleteOutlinedIcon color="error" />
            </Button>
          </StyledBasketContent>
        ))}
      </Stack>
    </>
  );
};

const StyledBasketItem = styled("div", {
  shouldForwardProp: (prop) => prop !== "expanded",
})<{ expanded: Boolean }>(({ theme, expanded }) => ({
  borderRadius: 1,
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  padding: theme.spacing(1.5),
  position: "relative",
  cursor: "",
  transition: theme.transitions.create("backgroundColor", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "&:hover": {
    backgroundColor: theme.palette.backgroundAlpha.hover,
  },
  img: {
    borderRadius: "2px",
  },
  ...(expanded === true && {
    backgroundColor: theme.palette.backgroundAlpha.pressed,
    "&:hover": {
      backgroundColor: theme.palette.backgroundAlpha.pressed,
    },
  }),
}));

const StyledBasketContent = styled(Stack)(({ theme }) => ({
  wordBreak: "break-word",
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "row",
  padding: `${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  borderRadius: 4,
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  transition: theme.transitions.create("background", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  "&:hover": {
    backgroundColor: theme.palette.backgroundAlpha.hover,
    ".MuiButton-root": {
      opacity: 1,
    },
  },
  ".MuiButton-root": {
    opacity: 0,
    transition: theme.transitions.create("opacity", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

export default BasketItem;
